import { useState } from 'react'

import { ru, toBaseUnit } from '../../lib/physics'
import { Choice, Predict, Slider } from './common'

/**
 * Приставка — это множитель (урок 8): приставку заменяют её множителем, основную единицу
 * не трогают. У площади множитель возводят в квадрат, у объёма — в куб. На рисунке —
 * сколько маленьких квадратиков (кубиков) укладывается в одном большом.
 */

const ПРИСТАВКИ = [
  { имя: 'милли', знак: 'м', степень: -3 },
  { имя: 'санти', знак: 'с', степень: -2 },
  { имя: 'деци', знак: 'д', степень: -1 },
  { имя: 'кило', знак: 'к', степень: 3 },
] as const
const МЕРНОСТИ = [
  { value: 1 as const, label: 'длина', знак: '' },
  { value: 2 as const, label: 'площадь', знак: '²' },
  { value: 3 as const, label: 'объём', знак: '³' },
]
const СТЕПЕНИ = '⁰¹²³⁴⁵⁶⁷⁸⁹'

/** 10 в степени — надстрочными цифрами: 10⁻⁶. */
function степень10(n: number): string {
  const s = String(Math.abs(n)).split('').map((ц) => СТЕПЕНИ[Number(ц)]).join('')
  return `10${n < 0 ? '⁻' : ''}${s}`
}

export default function PrefixLab() {
  const [число, setЧисло] = useState(5)
  const [п, setП] = useState(0)
  const [мерность, setМерность] = useState<1 | 2 | 3>(2)

  const пр = ПРИСТАВКИ[п]
  const з = МЕРНОСТИ[мерность - 1].знак
  const итог = toBaseUnit(число, пр.степень, мерность)
  const общая = пр.степень * мерность
  // рисунок: дециметр в метре — 10 делений по стороне; у сантиметра — 100 (рисуем 10×10 крупнее)
  const деленийНаСторону = 10

  return (
    <div className="card">
      <div className="lab-big">
        {число} {пр.знак}м{з} = {число}·({степень10(пр.степень)} м){з || ''} = {число}·{степень10(общая)} м{з}
      </div>
      {Math.abs(итог) >= 1e-3 && Math.abs(итог) < 1e7 && <div className="lab-caption">= {ru(итог, Math.max(0, -общая))} м{з}</div>}
      <div className="lab-caption">
        {пр.степень < 0
          ? `в 1 м${з} укладывается ${степень10(-общая)} ${пр.знак}м${з}`
          : `1 ${пр.знак}м${з} = ${степень10(общая)} м${з}`}
      </div>

      <svg className="lab-svg" style={{ maxWidth: 280, margin: '0 auto' }} viewBox="0 0 220 150" role="img" aria-label="Большая единица из маленьких">
        {мерность === 1 &&
          Array.from({ length: деленийНаСторону }, (_, i) => (
            <rect key={i} x={10 + i * 20} y={60} width={20} height={20} className={i % 2 ? 'fill-unit-b' : 'fill-unit-a'} />
          ))}
        {мерность >= 2 &&
          Array.from({ length: деленийНаСторону * деленийНаСторону }, (_, i) => (
            <rect key={i} x={60 + (i % 10) * 10} y={20 + Math.floor(i / 10) * 10} width={10} height={10} className={(i + Math.floor(i / 10)) % 2 ? 'fill-unit-b' : 'fill-unit-a'} />
          ))}
        {мерность === 3 && (
          <path d="M60 20 l20 -14 h100 l-20 14 M160 20 l20 -14 v100 l-20 14" className="ink" fill="none" strokeWidth={1.2} />
        )}
        <text x={110} y={140} textAnchor="middle" className="lab-tick">
          {мерность === 1 ? 'для деци: 1 м = 10 дм' : мерность === 2 ? 'для деци: 1 м² = 10 × 10 = 100 дм²' : 'для деци: 1 м³ = 10 × 10 × 10 = 1000 дм³'}
        </text>
      </svg>

      <Slider label="Число" value={число} min={1} max={9} step={1} unit="" onChange={setЧисло} />
      <div className="lab-sub">Приставка</div>
      <Choice options={ПРИСТАВКИ.map((x, i) => ({ value: i, label: `${x.имя} (${степень10(x.степень)})` }))} value={п} onChange={setП} />
      <div className="lab-sub">Что переводим</div>
      <Choice options={МЕРНОСТИ.map(({ value, label }) => ({ value, label }))} value={мерность} onChange={setМерность} />

      <Predict
        question="Сколько квадратных метров в 5 мм²?"
        options={['5·10⁻³ м²', '5·10⁻⁶ м²', '5·10⁻⁹ м²']}
        right={1}
        revealed={п === 0 && мерность === 2 && число === 5}
        explain="Миллиметр — 10⁻³ м, а в квадрате и множитель в квадрате: (10⁻³)² = 10⁻⁶. Показатели степени умножают на 2."
      />
      <p className="muted small">
        Порядок всегда один: число переписать, приставку заменить множителем, основную единицу не трогать.
        Для площади — скобка в квадрате, для объёма — в кубе. 1 л = 1 дм³ = 10⁻³ м³.
      </p>
    </div>
  )
}
